﻿@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.dashboard

import org.example.project.manager.NodeScope

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.example.project.service.GlobalManager
import org.example.project.service.LizenzService
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event

private fun dashboardScreenFromHash(): String =
    window.location.hash
        .substringAfter('?', "")
        .split("&")
        .firstOrNull { it.substringBefore("=").trim() == "screen" }
        ?.substringAfter("=", "")
        ?.let { js("decodeURIComponent")(it) as String }
        .orEmpty()

private fun dashboardQueryParamFromHash(name: String): String =
    window.location.hash
        .substringAfter('?', "")
        .split("&")
        .firstOrNull { it.substringBefore("=").trim() == name }
        ?.substringAfter("=", "")
        ?.let { js("decodeURIComponent")(it) as String }
        .orEmpty()

private fun dashboardScreenInstanceKey(): String {
    val screen = dashboardScreenFromHash().ifBlank { return "" }
    return when (screen) {
        "KontaktKalender" -> {
            val firmenId = dashboardQueryParamFromHash("firmenId").ifBlank { "self" }
            val userId = dashboardQueryParamFromHash("userId").ifBlank { "self" }
            "$screen#$firmenId#$userId"
        }
        "SchulungsFlowScreen" -> {
            val schulungId = dashboardQueryParamFromHash("schulungId").ifBlank { "missing" }
            "$screen#$schulungId"
        }
        "SYSVertriebsDashboard", "VertriebsDashboard" -> {
            "$screen#${dashboardQueryParamFromHash("linkedinContact")}#${dashboardQueryParamFromHash("callProspect")}#${dashboardQueryParamFromHash("linkedinCheckpoint")}#${dashboardQueryParamFromHash("salesNavigation")}"
        }
        else -> screen
    }
}

private fun clearDashboardHashQueryPreservingRoute() {
    val cleanHash = window.location.hash.substringBefore("?")
    val cleanUrl = "${window.location.pathname}${window.location.search}$cleanHash"
    window.asDynamic().history.replaceState(null, "", cleanUrl)
}

private fun licenseControlledScreenKey(key: String): String =
    when (key) {
        "KontaktKalender" -> "Kalender"
        "SchulungsFlowScreen" -> "Schulungsverwaltung"
        else -> key
    }

private fun accessControlledScreenKey(key: String): String =
    if (key == "KontaktKalender") "Kalender" else key

@Composable
fun DashboardScreen(
    node: String,
    onLogout: () -> Unit
) {
    var selectedScreen by remember { mutableStateOf(dashboardScreenFromHash()) }
    var selectedScreenInstanceKey by remember { mutableStateOf(dashboardScreenInstanceKey()) }
    var forcedOpenMode by remember { mutableStateOf(dashboardQueryParamFromHash("openMode")) }
    val profileScreen = remember(node) { ScreenManager.personalProfileScreen(node) }
    val companyIdentityScreen = remember(node) { ScreenManager.companyIdentityScreen(node) }
    val standortLayoutScreen = remember(node) { ScreenManager.standortLayoutScreen(node) }
    val kontaktKalenderScreen = remember(node) { ScreenManager.kontaktKalenderScreen(node) }
    val loginUser = LoginService.getUser()
    val sessionConfigVersion = GlobalManager.sessionConfigVersion()
    val art = when (node) {
        DashboardNode.CUS, DashboardNode.CUS_MOBILE -> NodeScope.CUS
        DashboardNode.NET -> NodeScope.NET
        else -> NodeScope.SYS
    }

    val resolved = remember(node, sessionConfigVersion) { ScreenManager.resolveDashboard(node) }
    val usesCusLizenz = node == DashboardNode.CUS || node == DashboardNode.CUS_MOBILE
    val allowedByLiz = if (usesCusLizenz) LizenzService.getEffectiveScreens().toSet() else emptySet()
    fun hasModuleShopAccess(): Boolean = listOf("Modul Shop", "MeineModule").any {
        it in allowedByLiz && RollenService.hasReadPermission(it)
    }
    fun hasAccessibleCusChild(key: String): Boolean =
        if (key == "VertriebsDashboard") {
            key in allowedByLiz && ScreenManager.childScreensForKey(key, DashboardNode.CUS).any {
                RollenService.hasReadPermission(it.key)
            }
        } else ScreenManager.childScreensForKey(key, DashboardNode.CUS).any {
            it.key in allowedByLiz && RollenService.hasReadPermission(it.key)
        }
    fun isNodeAllowed(key: String): Boolean =
        if (usesCusLizenz && key == "Modul Shop") hasModuleShopAccess()
        else if (usesCusLizenz && ScreenManager.isCusNavigationDashboard(key)) hasAccessibleCusChild(key)
        else if (usesCusLizenz) key == "MobileAnsichten" || licenseControlledScreenKey(key) in allowedByLiz else true
    fun hasReadAccess(key: String): Boolean =
        when {
            usesCusLizenz && key == "Modul Shop" -> hasModuleShopAccess()
            usesCusLizenz && ScreenManager.isCusNavigationDashboard(key) -> hasAccessibleCusChild(key)
            key == "SchulungsFlowScreen" -> loginUser?.zugangDesktop == true
            node == DashboardNode.NET -> true
            else -> RollenService.hasReadPermission(accessControlledScreenKey(key))
        }
    fun isNetworkFeatureAllowed(key: String): Boolean = true
    val canOpenProfile = true

    DisposableEffect(node) {
        val listener: (Event) -> Unit = {
            val target = dashboardScreenFromHash()
            selectedScreenInstanceKey = dashboardScreenInstanceKey()
            forcedOpenMode = dashboardQueryParamFromHash("openMode")
            if (target.isNotBlank()) selectedScreen = target
        }
        window.addEventListener("hashchange", listener)
        onDispose { window.removeEventListener("hashchange", listener) }
    }

    val screens = resolved.copy(
        lizenzen = resolved.lizenzen.filter { screen ->
            isNodeAllowed(screen.key) && isNetworkFeatureAllowed(screen.key) && hasReadAccess(screen.key)
        },
        preise = resolved.preise.filter { screen ->
            isNodeAllowed(screen.key) && isNetworkFeatureAllowed(screen.key) && hasReadAccess(screen.key)
        },
        module = resolved.module.mapNotNull { category ->
            val visibleScreens = category.columnSettings.filter { screen ->
                isNodeAllowed(screen.key) && isNetworkFeatureAllowed(screen.key) && hasReadAccess(screen.key)
            }
            category.takeIf { visibleScreens.isNotEmpty() }?.copy(columnSettings = visibleScreens)
        }
    )
    val contentScreens = screens
    val navigationCategories = contentScreens.module.associate { category ->
        category.bezeichnung to category.columnSettings.distinctBy { it.key }
    }
    val navigationSidebarScreens = contentScreens.preise.distinctBy { it.key }
    val visibleScreenKeys = remember(contentScreens, canOpenProfile, profileScreen.key, selectedScreen) {
        buildSet {
            addAll(contentScreens.lizenzen.map { it.key })
            addAll(contentScreens.preise.map { it.key })
            addAll(contentScreens.module.flatMap { it.columnSettings }.map { it.key })
            add(companyIdentityScreen.key)
            add(standortLayoutScreen.key)
            if (canOpenProfile) add(profileScreen.key)
            if (selectedScreen == "SchulungsFlowScreen" && isNodeAllowed("SchulungsFlowScreen") && hasReadAccess("SchulungsFlowScreen")) {
                add("SchulungsFlowScreen")
            }
        }
    }

    fun preferredStartScreen(): String =
        LoginService.getUser()
            ?.startbildschirm
            .orEmpty()
            .takeIf { it in visibleScreenKeys }
            .orEmpty()

    LaunchedEffect(visibleScreenKeys) {
        val preferred = preferredStartScreen()
        if (selectedScreen.isBlank() && preferred.isNotBlank()) {
            selectedScreenInstanceKey = preferred
            selectedScreen = preferred
        } else if (selectedScreen.isNotBlank() && selectedScreen !in visibleScreenKeys) {
            selectedScreenInstanceKey = preferred
            selectedScreen = preferred
        }
    }

    LaunchedEffect(node, usesCusLizenz) {
        if (!usesCusLizenz) return@LaunchedEffect
        val lastEffectiveScreens = LizenzService.getEffectiveScreens().toSet()
        runCatching { LizenzService.loadKundenModuleState() }
        val nextEffectiveScreens = LizenzService.getEffectiveScreens().toSet()
        if (nextEffectiveScreens != lastEffectiveScreens) {
            GlobalManager.triggerSessionConfigReload()
        }
    }

    Div {
        DashboardLayout(
            art = node,
            topBarScreens = contentScreens.lizenzen,
            categories = navigationCategories,
            categoryIcons = contentScreens.categoryIcons,
            sidebarScreens = navigationSidebarScreens,
            sidebarMode = contentScreens.typ.ifBlank { SidebarMode.FLAT },
            selectedKey = selectedScreen,
            selectedInstanceKey = selectedScreenInstanceKey,
            contentOnlyMode = false,
            onSelect = { key ->
                if (window.location.hash.contains("?")) {
                    clearDashboardHashQueryPreservingRoute()
                }
                selectedScreenInstanceKey = key
                forcedOpenMode = ""
                selectedScreen = key
            },
            onHome = {
                if (window.location.hash.contains("?")) {
                    clearDashboardHashQueryPreservingRoute()
                }
                forcedOpenMode = ""
                val preferred = preferredStartScreen()
                selectedScreenInstanceKey = preferred
                selectedScreen = preferred
            },
            onLogout = {
                LoginService.logout()
                onLogout()
            },
            forceContentScreenKey = selectedScreen.takeIf { forcedOpenMode == "content" },
            extraScreens = buildList {
                add(companyIdentityScreen)
                add(standortLayoutScreen)
                if (canOpenProfile) add(profileScreen)
                if (selectedScreen == "KontaktKalender") add(kontaktKalenderScreen)
                if (selectedScreen == "SchulungsFlowScreen") add(ScreenManager.schulungFlowScreen(node))
            },
            profilePopupKey = profileScreen.key,
            onOpenProfile = if (canOpenProfile) {
                {
                    selectedScreenInstanceKey = profileScreen.key
                    forcedOpenMode = ""
                    selectedScreen = profileScreen.key
                }
            } else {
                null
            }
        )
        ServiceDocumentViewerOverlayLayer()
    }
}
